import { Component, Input } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import {
  AvailableFacadeMaterial,
  CuttingFacadeMaterial,
  CuttingMaterial,
  ICuttingBaseMaterial,
} from 'cloud-shared-lib';
import { Observable, filter, from, map, of, switchMap, take, tap } from 'rxjs';
import { BazisMaterialHelper } from '../../../helpers/bazis-material.helper';
import { CuttingButt } from '../../../models/cutting/cutting-butt.model';
import { FacadeView } from '../../../models/cutting/facade-view.model';
import { CuttingButtFormService } from '../../../services/cutting-butt-form.service';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import {
  CuttingMaterialBaseDialogComponent,
  CuttingMaterialBaseDialogData,
} from '../../cutting-material-base-dialog/cutting-material-base-dialog.component';
import {
  EdgeInColorDialogData,
  EdgesInColorDialogComponent,
} from '../../edges-in-color-dialog/edges-in-color-dialog.component';
import {
  MaterialBaseButtsDialogComponent,
  MaterialBaseEdgeDialogData,
} from '../../material-base-butts-dialog/material-base-butts-dialog.component';
import { FacadeAvailableFilmMaterialsService } from '../facade-available-film-materials.service';
import { FacadeSettingsStateService } from '../facade-settings-state.service';
import { FacadesAvailableComponentsService } from '../facades-available-components.service';

export class MaterialsBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _MaterialsMixinBase = mixinDisabled(MaterialsBase);

@Component({
  selector: 'csl-materials',
  templateUrl: './materials.component.html',
  styleUrls: ['./materials.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
})
export class MaterialsComponent extends _MaterialsMixinBase implements CanDisable {
  @Input() showMaterialsPrice: boolean;
  @Input() currencySymbol = '';
  @Input() showEdge = true;
  @Input() showQuantityInStock: boolean;

  action$: Observable<any>;
  panel$: Observable<FacadeView>;

  defaultFilmDescription = $localize`Выберите материал пленки`;
  defaultEdgeDescription = $localize`Выберите материал облицовки кромки`;
  defaultMaterialDescription = $localize`Выберите материал фасада`;

  constructor(
    private dialogs: LegacyDialogsService,
    private facadeSettingsService: FacadeSettingsStateService,

    private cuttingButtFormService: CuttingButtFormService,
    private facadesAvailableComponentsService: FacadesAvailableComponentsService,
    private facadeAvailableFilmMaterialsService: FacadeAvailableFilmMaterialsService
  ) {
    super();

    this.panel$ = this.facadeSettingsService.panel$;
  }

  async selectMaterial(): Promise<void> {
    let panel: FacadeView;

    this.action$ = this.facadeSettingsService.panel$.pipe(
      take(1),
      tap(p => (panel = p)),
      switchMap(() =>
        this.dialogs.component<CuttingMaterialBaseDialogData, AvailableFacadeMaterial[]>(
          CuttingMaterialBaseDialogComponent,
          {
            showPrices: this.showMaterialsPrice,
            currencyName: this.currencySymbol,
            materialsToExclude: [],
            multiple: false,
            dataProvider: this.facadesAvailableComponentsService,
            showQuantityInStock: this.showQuantityInStock,
          },
          1150
        )
      ),
      filter(r => !!r?.length),
      tap(async m => {
        panel.material = m[0];

        this.facadeSettingsService.updatePanel(panel);

        const matchedButts = await this.cuttingButtFormService.getMatchedButtsForClient(
          panel.material.inMaterialBaseId,
          panel.material.thickness
        );

        if (panel.material.film) {
          this.resetEdges(panel);
        } else {
          panel.panelOutline.filmMaterial = null;
        }

        if (matchedButts.length === 1) {
          this.applyEdge(panel, matchedButts[0]);
        }
        this.facadeSettingsService.updatePanel(panel);
      })
    );
  }

  selectEdgeMaterial(): void {
    this.action$ = this.facadeSettingsService.panel$.pipe(
      take(1),
      switchMap(p => this.getEdge(p.material).pipe(map(e => ({ panel: p, edge: e })))),
      filter(r => !!r.edge),
      tap((r: { panel: FacadeView; edge: CuttingButt }) => {
        r.panel.l1 = r.edge;
        r.panel.l2 = r.edge;
        r.panel.w1 = r.edge;
        r.panel.w2 = r.edge;

        this.facadeSettingsService.updatePanel(r.panel);
      })
    );
  }

  selectFilmMaterial(): void {
    this.action$ = this.facadeSettingsService.panel$.pipe(
      take(1),
      switchMap(panel => this.getFilmMaterialsFromMatBase().pipe(map(f => ({ panel: panel, filmMaterials: f })))),
      filter(r => r?.filmMaterials.length > 0),
      tap((res: { panel: FacadeView; filmMaterials: CuttingMaterial[] }) => {
        res.panel.panelOutline.filmMaterial = res.filmMaterials[0];
        this.facadeSettingsService.updatePanel(res.panel);
      })
    );
  }

  getMaterialName(material: CuttingFacadeMaterial): string {
    return material ? BazisMaterialHelper.formatMaterialNameAndArticle(material.name, material.article, '') : '';
  }

  getPathToTexture(material: ICuttingBaseMaterial): string {
    if (!material) {
      return '';
    }

    return material.cropTextureLink;
  }

  getEdgeColor(item: FacadeView): string {
    const edge = item?.l1 || item?.l2 || item?.w1 || item?.w2;
    return edge?.color || '';
  }

  isColoredEdge(item: FacadeView): boolean {
    const edge = item?.l1 || item?.l2 || item?.w1 || item?.w2;
    return !edge?.textureName && !!edge?.color;
  }

  private getEdge(material: CuttingFacadeMaterial): Observable<CuttingButt> {
    return (
      material
        ? from(this.cuttingButtFormService.getMatchedButtsForClient(material.inMaterialBaseId, material.thickness))
        : of(null)
    ).pipe(
      switchMap(edges => {
        if (edges?.length > 1) {
          return this.getButtInColor(material);
        } else {
          return this.getButtFromMatBase(material);
        }
      })
    );
  }

  private getButtInColor(material: CuttingFacadeMaterial): Observable<CuttingButt> {
    return this.dialogs.component<EdgeInColorDialogData, CuttingButt>(
      EdgesInColorDialogComponent,
      {
        panelThickness: material.thickness,
        materialInMatBaseId: material.inMaterialBaseId,
        showQuantityInStock: this.showQuantityInStock,
      },
      1000
    );
  }

  private getButtFromMatBase(material?: CuttingFacadeMaterial): Observable<CuttingButt> {
    return this.dialogs.component<MaterialBaseEdgeDialogData, CuttingButt>(
      MaterialBaseButtsDialogComponent,
      {
        panelThickness: material ? material.thickness : 0,
        showQuantityInStock: this.showQuantityInStock,
      },
      1000
    );
  }

  private resetEdges(panel: FacadeView): void {
    panel.l1 = null;
    panel.l2 = null;
    panel.w1 = null;
    panel.w2 = null;
  }

  private applyEdge(panel: FacadeView, edge: CuttingButt): void {
    panel.l1 = edge;
    panel.l2 = edge;
    panel.w1 = edge;
    panel.w2 = edge;
  }

  private getFilmMaterialsFromMatBase(): Observable<CuttingMaterial[]> {
    return this.dialogs.component<CuttingMaterialBaseDialogData, CuttingMaterial[]>(
      CuttingMaterialBaseDialogComponent,
      {
        showPrices: this.showMaterialsPrice,
        currencyName: this.currencySymbol,
        materialsToExclude: [],
        multiple: false,
        dataProvider: this.facadeAvailableFilmMaterialsService,
        showQuantityInStock: this.showQuantityInStock,
      },
      1150
    );
  }
}
